<template>
    <hts-drawer
        v-model="visible"
        color="main"
        :submit="!disabled ? submit : undefined"
        :title="$t('labels.workflowPosition')"
        size="large"
        :rules="dataRule"
        @close="close">
        <hts-card
            :style="{ minWidth: minWidthScroll }"
            :title="$t('labels.generalInformation')">
            <hts-form-input
                v-model="data.index"
                :label="$t('labels.position')"
                :readonly="!edit"
                :disabled="edit"
                type="number" />

            <hts-form-input
                v-model="data.nodeTypeName"
                :label="$t('labels.nodeType')"
                :readonly="!edit"
                :disabled="edit"
                required />

            <div class="mt-5 mx-1">
                <p>
                    {{ $t('labels.businessAndTask') }} <span
                        v-if="edit"
                        class="text-error-500">
                        *
                    </span>
                </p>
            </div>
            <div
                v-for="(businessId, index) in Object.keys(data.businesses)"
                :key="businessId"
                class="pb-1">
                <hts-form-disclosure
                    :ref="
                        (el) => {
                            disclosureRefs[index] = el
                        }
                    "
                    :key="index"
                    color="main"
                    size="large"
                    :is-have-children="true">
                    <template #reference>
                        <div class="flex align-middle items-center gap-3 min-h-10 rounded-lg text-white bg-main-500 hover:bg-main-600 w-full">
                            <input
                                type="checkbox"
                                checked
                                class="w-[17.5px] h-[17.5px]"
                                disabled
                                :value="businessId" >
                            <span>
                                {{ getName(businessId, 'business', 'businessName') }}
                            </span>
                        </div>
                    </template>
                    <template #children>
                        <div
                            v-for="(taskId, subIndex) in data.businesses[businessId]"
                            :key="taskId"
                            class="flex align-middle items-center gap-3 min-h-10 rounded-lg text-white mt-3 ml-10 p-2 pl-3 bg-main-400 hover:bg-main-600">
                            <input
                                class="w-[17.5px] h-[17.5px]"
                                type="checkbox"
                                disabled
                                checked
                                :value="taskId" >
                            <span>
                                {{ getName(taskId, 'task', 'taskName') }}
                            </span>
                        </div>
                    </template>
                </hts-form-disclosure>
            </div>

            <div class="mt-5 mx-1 flex justify-start">
                <div>
                    {{ $t('labels.settingAndOrganizingTasks') }}
                </div>
                <hts-tooltip>
                    <div>
                        <span class="font-bold">
                            {{ $t('labels.dragTaskToSortingTask') }}
                        </span>
                    </div>
                    <template #reference>
                        <hi-information-circle-icon class="flex items-center text-green-500 w-6 h-6" />
                    </template>
                </hts-tooltip>
            </div>

            <div
                :class="{ 'mt-2': index !== 0 }"
                class="flex items-center">
                <div class="w-12 font-bold" />
                <div class="px-3 py-1 box-border rounded-xl w-full flex items-center">
                    <div class="w-7" />
                    <div class="w-full ml-2">
                        <p class="font-bold">
                            {{ $t('labels.task') }}
                        </p>
                    </div>

                    <div class="flex justify-end gap-5 w-full font-bold">
                        <div class="h-full flex items-center justify-start w-52">
                            {{ $t('labels.performedBy') }}
                        </div>

                        <div class="h-full flex items-center justify-start w-52">
                            {{ $t('labels.activateVehicleStatus') }}
                        </div>

                        <div
                            class="flex items-center justify-start w-48"
                            style="pointer-events: auto !important">
                            {{ $t('labels.completionTimeHour') }}
                        </div>
                    </div>
                </div>
            </div>
            <hts-draggable
                v-if="data.tasks && data.tasks.length"
                v-model="data.tasks"
                :style="{ 'pointer-events': 'none' }"
                :options="dragOptions">
                <div
                    v-for="(task, index) in data.tasks"
                    :key="task.key"
                    class="task-item">
                    <div
                        :class="{ 'mt-2': index !== 0 }"
                        class="flex items-center">
                        <div class="w-10 font-bold">
                            {{ index + 1 }}
                        </div>
                        <div class="bg-default-100 dark:bg-default-600 px-3 py-1 box-border rounded-xl w-full flex items-center">
                            <div class="w-7">
                                <hi-bars-3-icon
                                    class="h-6 w-6"
                                    aria-hidden="true" />
                            </div>
                            <div class="w-full ml-2">
                                <p class="font-bold">
                                    {{ task.taskName }}
                                </p>
                                <p class="text-sm opacity-75 italic">
                                    {{ task.businessName }}
                                </p>
                            </div>

                            <div class="flex justify-end items-center gap-5 w-full">
                                <div class="h-full flex items-center justify-start w-52">
                                    <hts-dropdown
                                        class="flex items-center"
                                        :options="optionResponsibleForImplementation"
                                        :placement="'left'"
                                        label-key="label"
                                        :hover="false"
                                        :multiple-level="true"
                                        @click="(action) => handleResponsibleForImplementation(`${task.business}-${task.task}`, action)">
                                        <template #reference>
                                            <hts-tooltip class="mt-1">
                                                <div>
                                                    <span class="font-bold">
                                                        {{ $t('labels.responsibleForImplementation') }}
                                                    </span>
                                                </div>
                                                <template #reference>
                                                    <hts-form-button
                                                        v-if="['HEADWAY_DRIVER', 'HEADWAY', 'CUSTOMER'].includes(task.responsibleForImplementation)"
                                                        size="small">
                                                        <span>
                                                            {{ labelResponsibleForImplementation(task.responsibleForImplementation) }}
                                                        </span>
                                                    </hts-form-button>
                                                </template>
                                            </hts-tooltip>
                                        </template>
                                    </hts-dropdown>
                                </div>

                                <div class="h-full flex items-center justify-start w-52">
                                    <hts-dropdown
                                        class="flex items-center"
                                        :options="optionActivateVehicleStatus"
                                        :placement="'left'"
                                        label-key="label"
                                        :hover="false"
                                        @click="(action) => handleActivateVehicleStatus(`${task.business}-${task.task}`, action)">
                                        <template #reference>
                                            <hts-tooltip>
                                                <div>
                                                    <span class="font-bold">
                                                        {{ $t('labels.activateVehicleStatus') }}
                                                    </span>
                                                </div>
                                                <template #reference>
                                                    <hts-form-button
                                                        v-if="['HAVE_GOODS', 'HAVE_CONTAINER', 'EMPTY_VEHICLE'].includes(task.activateVehicleStatus)"
                                                        size="small"
                                                        text-transform="lowercase">
                                                        <span>
                                                            {{ activateVehicleStatusLabel(task.activateVehicleStatus) }}
                                                        </span>
                                                    </hts-form-button>
                                                </template>
                                            </hts-tooltip>
                                        </template>
                                    </hts-dropdown>
                                </div>

                                <div
                                    class="flex items-center justify-start w-48"
                                    style="pointer-events: auto !important">
                                    <hts-tooltip class="mt-1">
                                        <div>
                                            <span class="font-bold">
                                                {{ $t('labels.completionTime') }}
                                            </span>
                                        </div>
                                        <template #reference>
                                            <!--                        {{ task.value }} {{ $t('labels.hour') }} -->
                                            <hts-form-input
                                                v-model="task.timeComplete"
                                                size="small"
                                                type="number"
                                                autocomplete="off"
                                                :disabled="edit && task.responsibleForImplementation !== 'CUSTOMER'"
                                                :readonly="!edit"
                                                name="completionTime"
                                                :prop="'task' + index" />
                                        </template>
                                    </hts-tooltip>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </hts-draggable>
        </hts-card>
        <hts-modal
            :show="showPopupDeletedTask"
            :title="$t('labels.deleteTaskOrBusiness')"
            type="confirm"
            :content="$t('labels.deleteTaskOrBusinessPositionWarning')"
            @close="showPopupDeletedTask = false"
            @confirm="actionDeletedTask()" />
    </hts-drawer>
</template>
<script lang="ts" setup>
import { ITaskSetting, IWorkflowPosition } from '../../../admincp/workflow/-imports/form'

import screen from '~/components/utils/screen'

const breakpoint = screen()
const minWidthScroll = computed(() => {
  return `${breakpoint.value.thresholds.sm}px`
})

const { $disableReactive } = useNuxtApp()
const emits = defineEmits(['update', 'routingPositions'])
const store = $store()

const taskData: { [key: string]: any } = computed(() => {
  return store.data.getTasks
})

const businessData: { [key: string]: any } = computed(() => {
  return store.data.getBusinesses
})

const disclosureRefs = ref([] as any)

const data = ref<IWorkflowPosition>({
  index: 0,
  nodeType: '',
  nodeTypeName: '',
  businesses: {} as { [key: string]: string[] },
  tasks: []
})

const visible = ref(false)
// const businessData = ref<{[key: string]: any}[]>([])
const actionDeletedTask = ref<Function>(() => {})
const showPopupDeletedTask = ref(false)
const edit = ref(true)
const dataQuoteRequest = ref<{ [key: string]: any }>({})

const open = (positionData: IWorkflowPosition, itemQuoteRequest: { [key: string]: any }, disableData?: Boolean) => {
  dataQuoteRequest.value = itemQuoteRequest
  data.value = { ...positionData }
  getBusinessData(positionData)
  if (disableData === true) {
    edit.value = false
  }
  visible.value = true
}

const dragOptions = {
  animation: 200
}

const resetFields = () => {
  data.value = {
    index: 0,
    nodeType: '',
    nodeTypeName: '',
    businesses: {},
    tasks: []
  }
  // businessData.value = [] as any
  edit.value = true
}

const close = () => {
  resetFields()
  visible.value = false
}

const submit = () => {
  if (!Object.keys(data.value?.businesses).length) {
    process.client && $toast().error($t('labels.pleaseSelectBusiness'))
    return
  }
  if (!data.value?.tasks?.length) {
    process.client && $toast().error($t('labels.pleaseSelectTask'))
    return
  }

  emits('update', $disableReactive(data.value) as IWorkflowPosition)
  close()
}

const getBusinessData = (positionData: IWorkflowPosition) => {
  data.value.tasks = positionData.tasks
  data.value.businesses = positionData.businesses
}

const businessClick = (e: any, id: string, business: any, index: number) => {
  e.stopPropagation()
  if (!edit.value) {
    return
  }

  if (!Object.keys(data.value.businesses).includes(id)) {
    const taskIds = business.tasks.map((task: any) => task._id)
    data.value.businesses[id] = taskIds
    taskIds.forEach((taskId: string) => {
      addTaskData(id, taskId)
    })
    if (disclosureRefs.value[index] && typeof disclosureRefs.value[index]?.expand === 'function') {
      disclosureRefs.value[index].expand()
    }
  } else {
    if (!showPopupDeletedTask.value) {
      showPopupDeletedTask.value = true
      actionDeletedTask.value = () => businessClick(e, id, business, index)
      e.preventDefault()
      return
    }

    const taskIds = data.value.businesses[id]
    const newObj = Object.keys(data.value.businesses).reduce((acc: any, key: any) => {
      if (key !== id) {
        acc[key] = data.value.businesses[key]
      }
      return acc
    }, {})
    taskIds.forEach((taskId: string) => {
      addTaskData(id, taskId, 'delete')
    })

    data.value.businesses = newObj

    showPopupDeletedTask.value = false
    actionDeletedTask.value = () => {}
  }
}

const taskClick = (e: any, businessId: string, taskId: string) => {
  if (!edit.value) {
    return
  }

  if (Object.keys(data.value.businesses).includes(businessId)) {
    if (!data.value.businesses[businessId].includes(taskId)) {
      data.value.businesses[businessId].push(taskId)
      addTaskData(businessId, taskId)
    } else {
      if (!showPopupDeletedTask.value) {
        if (e) {
          e.preventDefault()
        }
        showPopupDeletedTask.value = true
        actionDeletedTask.value = () => taskClick(e, businessId, taskId)
        return
      }

      data.value.businesses[businessId] = data.value.businesses[businessId].filter((item: any) => item !== taskId)
      addTaskData(businessId, taskId, 'delete')
    }
  } else {
    addTaskData(businessId, taskId)
    data.value.businesses[businessId] = [taskId]
  }

  actionDeletedTask.value = () => {}
  showPopupDeletedTask.value = false
}

const addTaskData = (businessId: string, taskId: string, action?: string) => {
  if (!edit.value) {
    return
  }

  const businessesData: { [key: string]: any } = store.data.getBusinesses
  const taskData: { [key: string]: any } = store.data.getTasks

  if (action === 'delete') {
    data.value.tasks = data.value.tasks.filter((task: any) => task.key !== `${businessId}-${taskId}`)
    return
  }

  const businessName = businessesData.filter((business: any) => businessId === business._id && !business.deleted)?.[0]?.name || ''
  const taskName = taskData.filter((task: any) => taskId === task._id && !task.deleted)?.[0]?.name || ''

  if (!businessName || !taskName) {
    return
  }

  data.value.tasks.push({
    key: `${businessId}-${taskId}`,
    business: businessId,
    businessName,
    task: taskId,
    taskName,
    responsibleForImplementation: '',
    activateVehicleStatus: '',
    timeConditions: []
  })
}

const handleActivateVehicleStatus = (key: string, actionData: any) => {
  if (!edit.value) {
    return
  }

  data.value.tasks.forEach((task: ITaskSetting) => {
    if (task.key === key) {
      task.activateVehicleStatus = actionData.value
    }
  })
}

const handleResponsibleForImplementation = (key: string, actionData: any) => {
  if (!edit.value) {
    return
  }

  data.value.tasks.forEach((task: ITaskSetting) => {
    if (task.key === key) {
      task.responsibleForImplementation = actionData.value
    }
  })
}

const getName = (id: string, keyId: string, keyName: string) => {
  const found: any = data.value.tasks.find((item: any) => item[keyId] === id)
  if (!found) {
    return ''
  }
  return found[keyName] || ''
}

const optionActivateVehicleStatus = computed(() => {
  if (dataQuoteRequest.value && dataQuoteRequest.value.transportation === 'CONTAINER') {
    return [
      {
        label: $t('labels.haveContainer'),
        class: 'text-blue-600 hover:text-blue-500 w-52 h-12',
        value: 'HAVE_CONTAINER'
      },
      {
        label: $t('labels.haveGoods'),
        class: 'text-blue-600 hover:text-blue-500 w-52 h-12',
        value: 'HAVE_GOODS'
      }
    ]
  }

  if (dataQuoteRequest.value.transportation === 'NON_CONTAINER') {
    return [
      {
        label: $t('labels.haveGoods'),
        class: 'text-blue-600 hover:text-blue-500 w-52 h-12',
        value: 'HAVE_GOODS'
      }
    ]
  }

  return []
})

const optionResponsibleForImplementation = computed(() => {
  return [
    {
      label: store.system.workspace.name,
      class: 'text-blue-600',
      tooltip: $t('labels.taskIsPerformedByDriverOrNot'),
      children: [
        {
          label: $t('labels.driver'),
          class: 'text-blue-600 hover:text-blue-500',
          value: 'HEADWAY_DRIVER'
        },
        {
          label: $t('labels.other'),
          class: 'text-blue-600 hover:text-blue-500',
          value: 'HEADWAY'
        }
      ]
    },
    {
      label: $t('labels.customer'),
      class: 'text-blue-600 hover:text-blue-500',
      value: 'CUSTOMER'
    }
  ]
})

const dataRule = computed(() => {
  const rule: { [key: string]: any } = {}
  data.value.tasks.forEach((task, index) => {
    const dataTask: { [key: string]: any } = taskData.value.find((iTask: { [key: string]: any }) => iTask._id === task.task)
    if (dataTask && dataTask.completionTimes) {
      const keyCompletionTime = dataQuoteRequest.value.transportation === 'CONTAINER' ? 'contCategory' : 'vehicleCategory'
      rule['task' + index] = `min_value:${
        dataTask.completionTimes &&
        dataTask.completionTimes[keyCompletionTime][dataQuoteRequest.value[keyCompletionTime]] &&
        dataTask.completionTimes[keyCompletionTime][dataQuoteRequest.value[keyCompletionTime]].minimumTime
          ? dataTask.completionTimes[keyCompletionTime][dataQuoteRequest.value[keyCompletionTime]].minimumTime
          : 0
      }`
    }
  })
  return rule
})

const labelResponsibleForImplementation = (type: string) => {
  const workspaceName = store.system.workspace.name
  if (type === 'HEADWAY') {
    return workspaceName + ' - ' + $t('labels.other')
  }
  if (type === 'HEADWAY_DRIVER') {
    return workspaceName + ' - ' + $t('labels.driver')
  }
  if (type === 'CUSTOMER') {
    return $t('labels.customer')
  }
}

const activateVehicleStatusLabel = (type: string) => {
  if (type === 'HAVE_GOODS') {
    return $t('labels.haveGoods')
  }
  if (type === 'HAVE_CONTAINER') {
    return $t('labels.haveContainer')
  }
  if (type === 'EMPTY_VEHICLE') {
    return $t('labels.emptyVehicle')
  }
}

defineExpose({
  open
})
</script>
